import { useCallback, useEffect, useMemo } from 'react';

import { Campaign } from 'src/common/interfaces/campaigns';

import { readStoredCampaigns, storeCampaigns } from '../helpers/campaign-storage';

export const useCampaignStorage = (current: Campaign | null) => {
  // Clean up obsolete campaigns from storage
  useEffect(() => {
    const storedCampaigns = readStoredCampaigns();
    const now = Date.now();
    const { seenCampaigns, activeCampaign } = storedCampaigns;
    const cleanedUp = seenCampaigns.filter((storedCampaign) => storedCampaign.endTime >= now);
    const activeCampaignToStore = activeCampaign && activeCampaign?.sessionExpiration > now ? activeCampaign : null;

    if (activeCampaignToStore === activeCampaign && seenCampaigns.length === cleanedUp.length) {
      return;
    }

    storeCampaigns({
      activeCampaign: activeCampaignToStore,
      seenCampaigns: cleanedUp,
    });
  }, []);

  const markAsSeen = useCallback(() => {
    if (!current) return;

    const storedCampaigns = readStoredCampaigns();
    const alreadySeen = storedCampaigns.seenCampaigns.some((c) => c.name === current.name);
    if (alreadySeen) {
      return;
    }

    storeCampaigns({
      ...storedCampaigns,
      seenCampaigns: [
        ...storedCampaigns.seenCampaigns,
        {
          name: current.name,
          endTime: current.endTime,
        },
      ],
    });
  }, [current]);

  const isSeen = useMemo(() => {
    if (!current) return false;

    return readStoredCampaigns().seenCampaigns.some((an) => an.name === current.name);
  }, [current]);

  const reset = () => {
    if (!current) return;

    const storedCampaigns = readStoredCampaigns();
    const removed = storedCampaigns.seenCampaigns.filter((campaign) => campaign.name !== current.name);

    storeCampaigns({
      ...storedCampaigns,
      seenCampaigns: removed,
    });
  };

  return { isSeen, markAsSeen, reset };
};
